// context/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance, { logout } from '../services/axiosInstance';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  // Initialize with false until we check with the server
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);
  const [error, setError] = useState(null);
  
  // Use ref to prevent stale closure issues with the checkAuthStatus function
  const authStateRef = useRef({
    isAuthenticated,
    user,
    hasCheckedAuth
  });
  
  // Update the ref when state changes
  useEffect(() => {
    authStateRef.current = {
      isAuthenticated,
      user,
      hasCheckedAuth
    };
  }, [isAuthenticated, user, hasCheckedAuth]);
  
  // Verify authentication status with backend
  const checkAuthStatus = useCallback(async (force = false) => {
    
    // Use the ref to get current values
    const { hasCheckedAuth } = authStateRef.current;
    
    if (!force && hasCheckedAuth) {
      return authStateRef.current;
    }
    
    try {
      const response = await axiosInstance.get('/auth/status/', {
        withCredentials: true,
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      
      if (response.status === 200 && response.data) {
        setIsAuthenticated(true);
        setUser(response.data);
        setHasCheckedAuth(true);
        setError(null);
        setLoading(false);
        return { isAuthenticated: true, user: response.data };
      }
    } catch (error) {
    
      // If we get a clear 401/403, user is not authenticated
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        setIsAuthenticated(false);
        setUser(null);
        setHasCheckedAuth(true);
        setError(null);
      } else {
        // For other errors like network issues, don't change auth state
        setError(error.message || 'Failed to check authentication status');
      }
      
      setLoading(false);
      return { isAuthenticated: false, user: null };
    }
  }, []); // No dependencies to prevent re-creation
  
  // Check auth status on mount and set up checks
  useEffect(() => {
    
    // Initial check
    checkAuthStatus(true);
    
    // Set up interval check every 5 minutes
    // Using a much longer interval (5 minutes = 300000ms)
    const intervalId = setInterval(() => {
      checkAuthStatus(true);
    }, 300000); // 5 minutes
    
    return () => {
      clearInterval(intervalId);
    };
  }, [checkAuthStatus]);
  
  // Handle auth expiration event
  useEffect(() => {
    const handleAuthExpired = () => {
      setIsAuthenticated(false);
      setUser(null);
    };
    
    window.addEventListener('authExpired', handleAuthExpired);
    return () => window.removeEventListener('authExpired', handleAuthExpired);
  }, []);

  // Log auth state changes
  useEffect(() => {
    // Console log removed
  }, [isAuthenticated]);

  return (
    <AuthContext.Provider 
      value={{ 
        isAuthenticated, 
        setIsAuthenticated, 
        user, 
        loading,
        checkAuthStatus,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
